import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CheckCheck } from "lucide-react";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, ConfirmModal } from "@/components/ui";
import { TruncatedText, focusRingInsetClass } from "@/components/ui";
import { BookingPartyItemsCell } from "./BookingPartyItemsCell";
import { BookingSellerHandshakeActions } from "./BookingSellerHandshakeActions";
import { PartnerBookingMobileBody } from "./PartnerBookingMobileBody";
import { PartnerReceiptActions } from "./PartnerReceiptActions";
import {
  partnerBookingMobileChrome,
  partnerBookingStatusBorderWidth,
  partnerHighlightCardClass,
} from "./partnerBookingChrome";
import {
  getPartnerBookingCardPresentation,
  getPartnerVisitDateTimeLabels,
} from "./partnerBookingCardPresentation";
import { partnerBookingStatusPaid } from "./partnerBookingStatusPaid";
import {
  needsCompletion,
  needsConfirmation,
  needsReceiptAction,
  pendingConfirmContext,
} from "./bookingActionRules";
import { bookingService } from "@/services/bookingService";
import { cn } from "@/lib/utils";
import type { BookingListRow } from "./bookingViewModel";
import { bookingPartyCount } from "./bookingPartyCount";

interface ManageBookingActionCardProps {
  booking: BookingListRow;
  detailPath: string;
  adminMode: boolean;
  canInteractHandshake: boolean;
  isProcessingReceipt: boolean;
  fileInputResetKey: number;
  onReceiptUpload: (file: File) => void;
  onManualAmountClick?: () => void;
}

export function ManageBookingActionCard({
  booking,
  detailPath,
  adminMode,
  canInteractHandshake,
  isProcessingReceipt,
  fileInputResetKey,
  onReceiptUpload,
  onManualAmountClick,
}: ManageBookingActionCardProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("bookings");
  const queryClient = useQueryClient();
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const { dateLabel, shortDateLabel, timeLabel } =
    getPartnerVisitDateTimeLabels(booking);
  const activityName = booking.activityName || t("unknownActivity");
  const { statusAccent, titleMuted, highlightCard } =
    getPartnerBookingCardPresentation(booking);
  const { statusBadge, paidAmount } = partnerBookingStatusPaid(booking);

  const handshakeSessionId = needsConfirmation(booking)
    ? booking.handshakeSessionId
    : null;
  const showComplete = needsCompletion(booking);
  const showReceiptCta = !handshakeSessionId && needsReceiptAction(booking);
  const hasActionColumn =
    !!handshakeSessionId || showReceiptCta || showComplete;

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      await bookingService.completeBooking(booking.id);
      toast.success(t("detail.markCompleteSuccess"));
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking", booking.id] });
      setShowCompleteConfirm(false);
    } catch (e) {
      toastApiError(e, "generic", t("detail.markCompleteError"));
      console.error(e);
    } finally {
      setIsCompleting(false);
    }
  };

  const timeClassName = cn(
    "text-base font-semibold tabular-nums leading-none tracking-tight",
    titleMuted ? "text-surface-400" : "text-surface-900",
  );

  const linkClassName = cn(
    "min-w-0 flex-1 transition-colors hover:bg-surface-50/70 dark:hover:bg-surface-100/40",
    focusRingInsetClass,
  );

  const actionColumnClassName =
    "flex w-full flex-row items-stretch gap-2 border-t border-surface-200/70 px-3 py-2.5 [&_button]:min-w-0 [&_button]:flex-1 [&_label]:flex [&_label]:min-w-0 [&_label]:flex-1 [&_label]:justify-center sm:w-auto sm:shrink-0 sm:items-center sm:border-l sm:border-t-0 sm:[&_button]:flex-none sm:[&_button]:w-auto sm:[&_label]:inline-flex sm:[&_label]:flex-none sm:[&_label]:w-auto";

  return (
    <Card
      className={cn(
        "hover:border-surface-300 sm:hover:shadow-elevated flex flex-col overflow-hidden transition-all duration-200 sm:flex-row sm:hover:-translate-y-px",
        partnerBookingStatusBorderWidth,
        partnerBookingMobileChrome,
        statusAccent,
        highlightCard && partnerHighlightCardClass,
        titleMuted && "bg-surface-100/80",
      )}
    >
      <Link
        to={detailPath}
        className={linkClassName}
      >
        <div className="sm:hidden">
          <PartnerBookingMobileBody
            booking={booking}
            titleMuted={titleMuted}
            visitDateLabel={dateLabel}
            visitTimeLabel={timeLabel}
            statusBadge={statusBadge}
            paidAmount={paidAmount}
            cancellationSource={null}
            className="py-2.5"
          />
        </div>

        <div className="hidden min-w-0 flex-1 items-center gap-4 px-4 py-3 sm:flex">
          <div className="w-20 shrink-0">
            <p className={timeClassName}>{timeLabel || "—"}</p>
            {shortDateLabel ? (
              <p
                className="text-surface-500 mt-1 truncate text-xs capitalize"
                title={dateLabel ?? undefined}
              >
                {shortDateLabel}
              </p>
            ) : null}
          </div>

          <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1">
            <div className="min-w-0 space-y-0.5">
              <TruncatedText
                as="p"
                className={cn(
                  "text-sm font-semibold",
                  titleMuted ? "text-surface-500" : "text-surface-900",
                )}
              >
                {booking.customerDisplayName}
              </TruncatedText>
              <TruncatedText
                as="p"
                className={cn(
                  "text-xs",
                  titleMuted ? "text-surface-400" : "text-surface-600",
                )}
              >
                {activityName}
              </TruncatedText>
            </div>

            <BookingPartyItemsCell
              booking={booking}
              variant="inline"
              className="text-surface-600 text-xs"
            />

            <div className="flex shrink-0 flex-col items-end gap-1">
              {statusBadge}
              {paidAmount}
            </div>
          </div>
        </div>
      </Link>

      {hasActionColumn ? (
        <div
          className={actionColumnClassName}
          onClick={(e) => e.stopPropagation()}
        >
          {handshakeSessionId ? (
            <BookingSellerHandshakeActions
              sessionId={handshakeSessionId}
              canInteract={canInteractHandshake}
              activityId={booking.activityId}
              partySize={bookingPartyCount(booking)}
              admin={adminMode}
              compact
              pendingConfirm={pendingConfirmContext(booking)}
            />
          ) : null}

          {showReceiptCta ? (
            <PartnerReceiptActions
              booking={booking}
              isProcessingReceipt={isProcessingReceipt}
              fileInputResetKey={fileInputResetKey}
              onReceiptUpload={onReceiptUpload}
              onManualAmountClick={onManualAmountClick}
              showExistingReceipt={false}
            />
          ) : null}

          {showComplete ? (
            <Button
              variant="primary"
              size="sm"
              className="whitespace-nowrap"
              leftIcon={<CheckCheck className="h-3.5 w-3.5" />}
              onClick={() => setShowCompleteConfirm(true)}
              disabled={isCompleting}
            >
              {isCompleting
                ? t("detail.markCompleteBusy")
                : t("detail.markComplete")}
            </Button>
          ) : null}
        </div>
      ) : null}

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}

        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={() => void handleComplete()}
        title={t("manageModal.completeConfirmTitle")}
        message={t("manageModal.completeConfirmMessage")}
        confirmText={t("detail.markComplete")}
        isLoading={isCompleting}
      />
    </Card>
  );
}
